/**
 * Protected Route Component
 * 
 * Protects routes based on user authentication and role requirements.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { isAuthenticated, getCurrentUser } from '../../services/authService';
import { getUserRole } from '../../utils/roleHelpers';
import { USER_ROLES, hasRolePermission, isValidRole } from '../../constants/roles';

/**
 * ProtectedRoute Component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|string[]} props.allowedRoles - Role(s) allowed to access this route
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 * @param {string} props.redirectTo - Where to redirect if access is denied
 */
export default function ProtectedRoute({
  children,
  allowedRoles = null,
  requireAuth = true,
  redirectTo = '/auth/sign-in',
}) {
  const toast = useToast();

  // Check if user is authenticated
  if (requireAuth && !isAuthenticated()) {
    toast({
      title: 'Authentication required',
      description: 'Please sign in to access this page.',
      status: 'warning',
      duration: 3000,
      isClosable: true,
    });
    return <Navigate to={redirectTo} replace />;
  }

  // If no role restriction, allow access
  if (!allowedRoles || (Array.isArray(allowedRoles) && allowedRoles.length === 0)) {
    return <>{children}</>;
  }

  // Get user role
  const userRole = getUserRole();
  const user = getCurrentUser();

  // Check if user has a valid role
  if (!userRole || !isValidRole(userRole)) {
    toast({
      title: 'Access denied',
      description: 'Invalid user role. Please contact support.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user is active
  if (user && user.is_active === false) {
    toast({
      title: 'Account deactivated',
      description: 'Your account has been deactivated. Please contact support.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return <Navigate to={redirectTo} replace />;
  }

  // Convert allowedRoles to array if it's a string
  const allowedRolesArray = Array.isArray(allowedRoles) 
    ? allowedRoles 
    : [allowedRoles];

  // Check if user role is in allowed roles
  const hasAccess = allowedRolesArray.some((role) => {
    // If role is a permission level, use hierarchy check
    if (role === 'admin_or_center_admin') {
      return userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.CENTER_ADMIN;
    }
    // Direct role match
    return userRole === role || hasRolePermission(userRole, role);
  });

  if (!hasAccess) {
    toast({
      title: 'Access denied',
      description: 'You do not have permission to access this page.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

